import { DrawHand } from '@/pages/common/train-handpose/drawhand';
import * as tf from '@tensorflow/tfjs';
import * as tmPose from '@teachablemachine/pose';
import JSZip from 'jszip';

/**
 * 查找某个匹配的路由
 * @param {*} path
 * @param {*} RootRoutes
 * @returns
 */
export const findRouterPath = (path, RootRoutes) => {
  for (let i = 0; i < RootRoutes.length; i++) {
    const route = RootRoutes[i];

    if (route.path == undefined) {
      continue;
    }

    if (route.path === path) {
      return route;
    }

    let pAy = route.path.split('/');
    let pAy2 = path.split('/');
    if (pAy[pAy.length - 1][0] == ':' && pAy.length == pAy2.length) {
      return route;
    }

    if (route.children) {
      const childRoute = findRouterPath(path, route.children);

      if (childRoute) {
        return childRoute;
      }
    }
  }

  return null;
};

/**
 * 保存训练模型
 * @param {*} model 模型对象
 * @param {*} modelName 模型名称
 * @returns
 */
export const saveTrainModel = (model, modelName) => {
  return new Promise((resolve) => {
    model = model.model || model;
    const customSaveHandler = tf.io.withSaveHandler(async (artifacts) => {
      let { weightData, weightSpecs, ...weightDataOther } = artifacts;
      weightDataOther.weightsManifest = [
        {
          paths: [`./${modelName}.weights.bin`],
          weights: weightSpecs,
        },
      ];

      return {
        weightData: weightData,
        weightDataOther,
      };
    });

    model.save(customSaveHandler).then(resolve);
  });
};

export const loadLocalTrainModel = () => {
  return new Promise((resolve, reject) => {
    let file = document.createElement('input');
    file.type = 'file';
    file.accept = '.zip';
    file.onchange = async (e) => {
      try {
        let file = e.target.files[0];
        let zipExt = getFileExt(file.name);

        if (zipExt !== 'zip') {
          reject(new Error('请上传zip文件'));
          return;
        }

        let jszip = new JSZip();
        let zipContent = await jszip.loadAsync(file);
        let files = zipContent.files;
        let modelFile = [];
        let mateData = {};

        let fileNames = Object.keys(files);
        for (let i = 0; i < fileNames.length; i++) {
          let fileName = fileNames[i];
          let ext = getFileExt(fileName);
          let name = fileName.replace(`.${ext}`, '');
          let data;
          if (ext == 'bin') {
            data = await files[fileName].async('arraybuffer');
          } else if (ext == 'json') {
            data = await files[fileName].async('text');
            let jsonData = JSON.parse(data);
            mateData = {
              labels: jsonData.userDefinedMetadata.outputLabels,
              userMetadata: jsonData.userDefinedMetadata,
            };
          }

          let file = dataToFileData(name, data, ext);
          modelFile.push(file);
        }

        // 模型文件不对，必须包含json文件跟bin文件
        if (modelFile.length !== 2) {
          reject(new Error('模型文件不正确'));
          return;
        }

        let model = await tf.io.browserFiles([modelFile[0], modelFile[1]]);

        resolve({
          model,
          mateData,
        });
      } catch (err) {
        reject(err);
      }
    };
    file.click();
    file.remove();
  });
};

const getFileExt = (fileName) => {
  let exts = fileName.split('.');
  return exts[exts.length - 1];
};

/**
 * 保存文件
 * @param {*} param0
 * fileName 文件名称
 * addFileCallback 添加文件回调
 */
export const saveFile = async ({
  fileName,
  addFileCallback,
  isSaveAsZip = true,
}) => {
  let jszip = new JSZip();

  await addFileCallback(jszip);

  let content = await jszip.generateAsync({
    type: 'blob',
  });
  if (isSaveAsZip) {
    downloadFile(fileName, content);
    return;
  } else {
    return content;
  }
};

export const downloadFile = (fileName, content) => {
  const a = document.createElement('a');
  const url = URL.createObjectURL(content);
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

const fileDataType = {
  json: 'application/json',
  bin: 'application/octet-stream',
  zip: 'application/zip',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  onnx: '',
};
export const dataToFileData = (fileName, data, type) => {
  let ext = fileName.endsWith(`.${type}`) ? '' : `.${type}`;
  return new File([data], `${fileName}${ext}`, {
    type: fileDataType[type],
  });
};

export const imageBase64ToArrayBuffer = (base64) => {
  let binary = atob(base64.split(',')[1]);
  let len = binary.length;
  let view = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    view[i] = binary.charCodeAt(i);
  }

  return view.buffer;
};

let minMaxValues = {
  min: [],
  max: [],
};
export const normalizeData = (data, isReset = false) => {
  if (isReset) {
    minMaxValues = {
      min: [],
      max: [],
    };
  }

  if (minMaxValues.min.length === 0 || minMaxValues.max.length === 0) {
    // 初始化最小值和最大值
    minMaxValues.min = Array(data[0].length).fill(Infinity);
    minMaxValues.max = Array(data[0].length).fill(-Infinity);

    // 计算最小值和最大值
    for (const sample of data) {
      for (let i = 0; i < sample.length; i++) {
        if (sample[i] < minMaxValues.min[i]) {
          minMaxValues.min[i] = sample[i];
        }
        if (sample[i] > minMaxValues.max[i]) {
          minMaxValues.max[i] = sample[i];
        }
      }
    }
  }

  // 归一化数据
  const normalizedData = [];
  for (const sample of data) {
    const normalizedSample = [];
    for (let i = 0; i < sample.length; i++) {
      const range = minMaxValues.max[i] - minMaxValues.min[i];
      if (range === 0) {
        normalizedSample.push(0);
      } else {
        normalizedSample.push((sample[i] - minMaxValues.min[i]) / range);
      }
    }
    normalizedData.push(normalizedSample);
  }

  return normalizedData;
};

export const getNormalizeMinMaxValue = () => {
  return minMaxValues;
};
export const setNormalizeMinMaxValue = (data) => {
  minMaxValues = data;
};

// 提取关键点数据
export const extractKeypoints = (landmarks) => {
  const keypoints = [];
  for (const point of landmarks) {
    keypoints.push(point.x, point.y, point.z);
  }
  return keypoints;
};

// 延迟执行
export const sleep = (time) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, time);
  });
};

export const cropTensor = (img, grayscaleModel, grayscaleInput) => {
  const size = Math.min(img.shape[0], img.shape[1]);
  const centerHeight = img.shape[0] / 2;
  const beginHeight = centerHeight - size / 2;
  const centerWidth = img.shape[1] / 2;
  const beginWidth = centerWidth - size / 2;

  if (grayscaleModel && !grayscaleInput) {
    let grayscale_cropped = img.slice(
      [beginHeight, beginWidth, 0],
      [size, size, 3],
    );

    grayscale_cropped = grayscale_cropped.reshape([size * size, 1, 3]);
    const rgb_weights = [0.2989, 0.587, 0.114];
    grayscale_cropped = tf.mul(grayscale_cropped, rgb_weights);
    grayscale_cropped = grayscale_cropped.reshape([size, size, 3]);

    grayscale_cropped = tf.sum(grayscale_cropped, -1);
    grayscale_cropped = tf.expandDims(grayscale_cropped, -1);

    return grayscale_cropped;
  }
  return img.slice([beginHeight, beginWidth, 0], [size, size, 3]);
};

export const capture = (rasterElement, grayscale) => {
  return tf.tidy(() => {
    const pixels = tf.browser.fromPixels(rasterElement);

    const cropped = cropTensor(pixels, grayscale);

    const batchedImage = cropped.expandDims(0);

    return batchedImage.toFloat().div(tf.scalar(127)).sub(tf.scalar(1));
  });
};

const uploadTypes = ['image/png', 'image/jpeg', 'image/jpg'];

/**
 *
 * @param {*} param0
 * @returns
 */
export const filesToImage = async ({
  files,
  model,
  uploadType,
  imageWidth = 224,
  imageHeight = 224,
  progressCallback,
}) => {
  let base64Array = [];

  if (!model) {
    throw new Error('基础模型未加载');
  }

  for (let i = 0; i < files.length; i++) {
    progressCallback &&
      progressCallback({
        currentIndex: i + 1,
        total: files.length,
      });
    let file = files[i];

    if (!uploadTypes.includes(file.type) || file.size > 5 * 1024 * 1024) {
      throw new Error('请上传小于5M，格式为JPG、PNG、JPEG的图片');
    }
    let { canvas, base64 } = await asyncFileToImage(
      file,
      imageWidth,
      imageHeight,
    );

    let tensor = await capture(canvas);
    // 如果是姿态，需要判断有没有姿态关键点
    if (uploadType == 'pose') {
      let { pose, posenetOutput } = await model.estimatePose(canvas);
      let ctx = canvas.getContext('2d');

      if (pose) {
        await tmPose.drawKeypoints(pose.keypoints, 0.5, ctx, 4, 'red', 'red');
        await tmPose.drawSkeleton(pose.keypoints, 0.5, ctx, 4, 'red');
        let sourceBase64 = canvas.toDataURL('image/png');
        base64Array.push({
          pose: pose,
          posenetOutput: posenetOutput,
          sourceBase64: base64,
          base64: sourceBase64,
          tensor,
        });
      }
      continue;
    } else if (uploadType == 'handpose') {
      let handpoint = await model.estimateHands(canvas);
      if (handpoint[0] && handpoint[0].keypoints3D) {
        let handposeDrawUtil = new DrawHand(canvas);
        handposeDrawUtil.drawResult(handpoint[0]);
        let sourceBase64 = canvas.toDataURL('image/png');
        base64Array.push({
          handPosetOutput: handpoint[0].keypoints3D,
          sourceBase64: base64,
          base64: sourceBase64,
          tensor,
        });
      }
      continue;
    }

    base64Array.push({
      tensor,
      base64,
    });
  }
  return base64Array;
};

export const asyncFileToImage = async (file, imageWidth, imageHeight) => {
  return new Promise((resolve, reject) => {
    let fileReader = new FileReader();

    fileReader.readAsDataURL(file);
    fileReader.onload = function (event) {
      // 裁剪图片
      let image = new Image();
      image.src = event.target.result;
      image.onload = function () {
        let canvas = document.createElement('canvas');
        let context = canvas.getContext('2d');
        canvas.width = imageWidth;
        canvas.height = imageHeight;

        let scale = Math.min(
          imageWidth / image.width,
          imageHeight / image.height,
        );

        let drawWidth = image.width * scale;
        let drawHeight = image.height * scale;
        let x = (imageWidth - drawWidth) / 2;
        let y = (imageHeight - drawHeight) / 2;

        context.fillStyle = 'white';
        context.fillRect(0, 0, imageWidth, imageHeight);
        context.drawImage(image, x, y, drawWidth, drawHeight);

        let newImage = canvas.toDataURL('image/png');
        resolve({
          base64: newImage,
          canvas: canvas,
        });
      };
      image.onerror = function (error) {
        reject(error);
      };
    };
    fileReader.onerror = function (error) {
      reject(error);
    };
  });
};

export const yixiaoMessage = (message) => {
  if (window.cFunNotifyAiWebErr) {
    window.cFunNotifyAiWebErr(`{errId:10001,msg:"${message}"}`);
  }
};

/**
 *
 * @param {number} byteSize
 */
export const byteFormat = (byteSize) => {
  let i = 0;
  let unit = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  while (byteSize >= 1024) {
    byteSize /= 1024;
    i++;
  }
  return `${byteSize.toFixed(1)}${unit[i]}`;
};

export const fetchImageUrlToFile = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], 'image.png', { type: 'image/png' });
};

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const unpack = async (url, option) => {
  const zip = new JSZip();
  const unpackData = await zip.loadAsync(url, option);
  return unpackData;
};

/**
 * 新窗口打开地址
 * @param {*} url
 * @param {*} option
 */
export const openBrowser = (url, option) => {
  if (window.OpenBrowser) {
    window.OpenBrowser(url);
  } else {
    window.open(url, option);
  }
};
